import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BionicText } from '../components/custom/BionicText';
import { StorePrice } from '../components/custom/StorePrice';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  ArrowLeft, 
  Loader2,
  ShoppingBag,
  Truck,
  Store,
  Check,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  produce: '🥬',
  dairy: '🥛',
  protein: '🥩',
  grains: '🌾',
  pantry: '🥫',
  frozen: '❄️',
  beverages: '🥤',
  other: '📦'
};

export default function GroceryList() {
  const { planId } = useParams();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [plan, setPlan] = useState(null);
  const [groceryData, setGroceryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});
  const [filterStore, setFilterStore] = useState('all');
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(planId || '');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (token) {
      fetchPlans();
    }
  }, [token]);

  useEffect(() => {
    if (token && selectedPlanId) {
      fetchGroceryList();
    }
  }, [token, selectedPlanId]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/diet-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.filter(p => p.grocery_list && p.grocery_list.length > 0));
      
      if (!selectedPlanId && response.data.length > 0) {
        const firstWithGrocery = response.data.find(p => p.grocery_list && p.grocery_list.length > 0);
        if (firstWithGrocery) {
          setSelectedPlanId(firstWithGrocery.plan_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchGroceryList = async () => {
    setLoading(true);
    try {
      const [planRes, groceryRes] = await Promise.all([
        axios.get(`${API_URL}/api/diet-plans/${selectedPlanId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/diet-plans/${selectedPlanId}/grocery-list`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPlan(planRes.data);
      setGroceryData(groceryRes.data);
    } catch (error) {
      console.error('Failed to fetch grocery list:', error);
      if (error.response?.status === 404) {
        toast.error('No grocery list found. Generate a meal plan first.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemName) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const getLowestPrice = (prices) => {
    if (!prices || prices.length === 0) return null;
    return prices.reduce((min, p) => p.price < min.price ? p : min, prices[0]);
  };

  const filterItems = (items) => {
    if (filterStore === 'all') return items;
    return items.filter(item => 
      item.prices?.some(p => p.store_name === filterStore && p.available)
    );
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const categories = groceryData?.by_category || {};
  const totalItems = groceryData?.items?.length || 0;
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <main className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 opacity-0 animate-fade-in">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-2 -ml-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                <BionicText>Grocery List</BionicText>
              </h1>
              {plan && (
                <p className="text-muted-foreground mt-1">
                  For: {plan.name}
                </p>
              )}
            </div>
            
            {totalItems > 0 && (
              <Badge variant="secondary" className="text-sm px-4 py-2 self-start">
                <Check className="h-4 w-4 mr-2" aria-hidden="true" />
                {checkedCount} / {totalItems} items
              </Badge>
            )}
          </div>
        </div>

        {/* Plan Selector & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {plans.length > 1 && (
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger className="w-full sm:w-[250px] rounded-xl" data-testid="plan-selector">
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.plan_id} value={p.plan_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={filterStore} onValueChange={setFilterStore}>
            <SelectTrigger className="w-full sm:w-[200px] rounded-xl" data-testid="store-filter">
              <Filter className="h-4 w-4 mr-2" aria-hidden="true" />
              <SelectValue placeholder="Filter by store" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              <SelectItem value="trader_joes">Trader Joe's</SelectItem>
              <SelectItem value="whole_foods">Whole Foods</SelectItem>
              <SelectItem value="sprouts">Sprouts</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Legend */}
        <Card className="mb-6 opacity-0 animate-fade-in stagger-2">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Delivery available</span>
              </div>
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Curbside pickup</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs border-primary text-primary">Best</Badge>
                <span className="text-muted-foreground">Lowest price</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grocery List by Category */}
        {Object.keys(categories).length === 0 ? (
          <Card data-testid="no-grocery-list">
            <CardContent className="py-12 text-center">
              <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg text-card-foreground mb-2">
                No grocery list yet
              </h3>
              <p className="text-muted-foreground mb-6">
                Generate a meal plan first to create your grocery list.
              </p>
              <Button 
                onClick={() => navigate('/diet-builder')}
                className="rounded-full"
                data-testid="create-plan-btn"
              >
                Create a Diet Plan
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Accordion type="multiple" defaultValue={Object.keys(categories)} className="space-y-4">
            {Object.entries(categories).map(([category, items], catIdx) => {
              const filteredItems = filterItems(items);
              if (filteredItems.length === 0) return null;
              
              return (
                <AccordionItem 
                  key={category} 
                  value={category}
                  className="border rounded-2xl px-4 opacity-0 animate-fade-in"
                  style={{ animationDelay: `${catIdx * 0.1}s` }}
                  data-testid={`category-${category}`}
                >
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl" aria-hidden="true">
                        {categoryIcons[category] || categoryIcons.other}
                      </span>
                      <span className="font-semibold capitalize text-foreground">
                        {category}
                      </span>
                      <Badge variant="secondary" className="ml-2">
                        {filteredItems.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-4">
                      {filteredItems.map((item, idx) => {
                        const lowestPrice = getLowestPrice(item.prices);
                        
                        return (
                          <div 
                            key={idx}
                            className={`p-4 rounded-xl border transition-all ${
                              checkedItems[item.name] 
                                ? 'bg-muted/50 border-muted' 
                                : 'bg-card border-border'
                            }`}
                            data-testid={`grocery-item-${item.name.replace(/\s+/g, '-')}`}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <Checkbox
                                id={`item-${idx}`}
                                checked={checkedItems[item.name] || false}
                                onCheckedChange={() => toggleItem(item.name)}
                                className="mt-1"
                                aria-label={`Mark ${item.name} as purchased`}
                              />
                              <div className="flex-1">
                                <label 
                                  htmlFor={`item-${idx}`}
                                  className={`font-medium cursor-pointer ${
                                    checkedItems[item.name] ? 'line-through text-muted-foreground' : 'text-foreground'
                                  }`}
                                >
                                  {item.name}
                                </label>
                                <p className="text-sm text-muted-foreground">
                                  {item.quantity} {item.unit}
                                </p>
                              </div>
                            </div>

                            {/* Store Prices */}
                            {item.prices && item.prices.length > 0 && (
                              <div className="space-y-2 ml-7">
                                {item.prices.map((price, priceIdx) => (
                                  <StorePrice
                                    key={priceIdx}
                                    store={price.store_name}
                                    price={price.price}
                                    available={price.available}
                                    deliveryAvailable={price.delivery_available}
                                    curbsideAvailable={price.curbside_available}
                                    storeUrl={price.store_url}
                                    isLowest={lowestPrice && price.price === lowestPrice.price}
                                  />
                                ))}
                              </div>
                            )}

                            {(!item.prices || item.prices.length === 0) && (
                              <p className="text-sm text-muted-foreground ml-7">
                                No price data available
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </main>
  );
}
